import { ArrowDownLeft, ArrowUpRight, ExternalLink, FileText, FolderOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Documento } from '@/lib/api/contract';
import {
  enCuantosCorreos,
  etiquetaDeDireccion,
  nombreVisible,
  tamanoLegible,
} from '@/lib/documentos/documentos';
import { soloDia } from '@/lib/fechas';
import { formatAbsolute } from './relative-time';

/**
 * Los documentos de una ficha, como filas.
 *
 * Vive acá y no en un módulo porque la dibujan dos fichas —la de la empresa y la
 * de la persona— y tiene que ser la misma fila en las dos: el mismo documento
 * visto desde su empresa y desde quien lo mandó no puede leerse distinto.
 *
 * EL ARCHIVO NO ESTÁ ACÁ. Vive en Google Drive y el nombre es un link que lo
 * abre allá, en otra pestaña: el CRM guarda el índice, no los bytes. Por eso no
 * hay «descargar» ni vista previa, y por eso un documento que llegó sin link se
 * dibuja igual —existe, se sabe cuándo y de qué lado vino— pero sin nada donde
 * hacer clic.
 */

const DESTINO = { target: '_blank', rel: 'noopener noreferrer' } as const;

/**
 * De qué lado vino, **con palabra y con forma, además del color**.
 *
 * La flecha sale hacia arriba en lo enviado y entra hacia abajo en lo recibido,
 * y al lado lo dice el texto: quien no distingue los dos tonos, o no ve ninguno,
 * lee lo mismo que los demás.
 */
function Direccion({ direccion }: { direccion: Documento['direccion'] }) {
  const etiqueta = etiquetaDeDireccion(direccion);
  if (!etiqueta) return null;
  const enviado = direccion === 'enviado';
  const Flecha = enviado ? ArrowUpRight : ArrowDownLeft;
  return (
    <Badge variant={enviado ? 'info' : 'secondary'} className="px-1.5 py-0">
      <Flecha aria-hidden className="size-3" />
      {etiqueta}
    </Badge>
  );
}

function FilaDeDocumento({ documento }: { documento: Documento }) {
  const nombre = nombreVisible(documento);
  const correos = enCuantosCorreos(documento.apariciones);

  return (
    <li className="flex items-start gap-3 rounded-lg border border-border p-3">
      <span className="grid size-8 shrink-0 place-items-center rounded-md bg-muted text-muted-foreground">
        <FileText aria-hidden className="size-4" />
      </span>

      <div className="min-w-0 flex-1">
        {documento.url ? (
          <a
            href={documento.url}
            {...DESTINO}
            // El nombre con que quedó en Drive, que es el que va a ver al abrirlo.
            title={documento.nombre}
            className="inline-flex max-w-full items-center gap-1 rounded-sm text-sm font-medium hover:text-brand hover:underline"
          >
            <span className="truncate">{nombre}</span>
            <ExternalLink aria-hidden className="size-3 shrink-0 text-muted-foreground" />
            <span className="sr-only">(se abre en Google Drive, en otra pestaña)</span>
          </a>
        ) : (
          <p className="truncate text-sm font-medium" title={documento.nombre}>
            {nombre}
          </p>
        )}

        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <time
            dateTime={documento.fecha || undefined}
            title={documento.fecha ? formatAbsolute(documento.fecha) : undefined}
          >
            {soloDia(documento.fecha, 'Sin fecha')}
          </time>
          <Direccion direccion={documento.direccion} />
          <span className="tabular">{tamanoLegible(documento.tamano)}</span>
          {correos && <span>{correos}</span>}
        </p>
      </div>
    </li>
  );
}

export interface ListaDeDocumentosProps {
  items: readonly Documento[];
}

export function ListaDeDocumentos({ items }: ListaDeDocumentosProps) {
  return (
    <ul className="space-y-2">
      {items.map((documento) => (
        <FilaDeDocumento key={documento.id} documento={documento} />
      ))}
    </ul>
  );
}

/** «Abrir carpeta en Drive»: la carpeta de la empresa, en otra pestaña. */
export function AbrirCarpetaEnDrive({ url }: { url: string }) {
  return (
    <Button variant="outline" size="sm" asChild>
      <a href={url} {...DESTINO}>
        <FolderOpen aria-hidden className="size-3.5" />
        Abrir carpeta en Drive
        <span className="sr-only">(se abre en otra pestaña)</span>
      </a>
    </Button>
  );
}
